import Vue from 'vue'
import Router from 'vue-router'
import { canAccess, hasPermission, hasAnyPermission } from './middlewares/hasAccessRouteMiddlewares'
import { hasFeature } from './middlewares/hasFeatureMiddleware'
import {lazyLoad} from './helpers/lazyLoad'
import { beforeEnterAuthedRoutes, beforeEnterPublicRoutes } from './middlewares/routesMiddlewares'
import multiguard from 'vue-router-multiguard'

Vue.use(Router)

const router = new Router({
  mode: 'history',
  base: '/',
  scrollBehavior () {
    return {
      x: 0,
      y: 0
    }
  },
  routes: [
    // Main layout routes
    {
      path: '',
      component: lazyLoad('layouts/main/Main'),
      name: 'main',
      children: [
        {
          path: '/targeting',
          name: 'targeting-view',
          component: lazyLoad('views/targeting/index'),
          children: [
            {
              path: 'audiences',
              name: 'audiences-view',
              component: lazyLoad('views/targeting/components/audiences')
            },
            {
              path: 'audiences/create',
              name: 'audiences-create',
              component: lazyLoad('views/targeting/components/create-audience')
            },
            {
              path: 'audiences/edit/:id',
              name: 'audiences-edit',
              component: lazyLoad('views/targeting/components/edit-audience')
            },
            {
              path: 'publishers',
              name: 'publishers-view',
              component: lazyLoad('views/targeting/components/publishers')
            }
          ]
        },

        {
          path: '/ads',
          name: 'ads-view',
          component: lazyLoad('views/ads-list/index')
        },
        {
          path: 'icons',
          name: 'icons',
          component: lazyLoad('views/speakol-icons/index')
        },
        {
          path: 'template',
          name: 'template',
          component: lazyLoad('views/speakol-template/index')
        },
        {
          path: '/',
          name: 'home',
          component: lazyLoad('views/home/index')
        },
        {
          path: '/conversions',
          name: 'conversions',
          component: lazyLoad('views/conversions/index')
        },
        {
          path: '/conversions/create',
          name: 'conversions-create',
          component: lazyLoad('views/conversions/components/CreateConversions')
        },
        {
          path: '/campaigns/',
          name: 'campaigns-index',
          component: lazyLoad('views/campaigns/index/Index'),
          beforeEnter: hasPermission('list_personal_campaigns')
        },
        {
          path: '/campaigns/admin',
          name: 'campaigns-index-admin',
          component: lazyLoad('views/campaigns/index/AdminIndex'),
          beforeEnter: hasPermission('list_all_campaigns')
        },
        {
          path: '/campaigns/create',
          name: 'campaigns-create',
          component: lazyLoad('views/campaigns/create/Index'),
          beforeEnter: hasPermission('create_campaigns')
        },
        {
          path: '/campaigns/success/:id/',
          name: 'campaigns-success',
          component: lazyLoad('views/campaigns/create/Success'),
          beforeEnter: hasPermission('create_campaigns')
        },
        {
          path: '/campaigns/:id/insights',
          name: 'campaigns-insights',
          component: lazyLoad('views/campaigns/insights/Insights'),
          beforeEnter: hasAnyPermission('create_campaigns', 'campaigns_insights')
        },
        {
          path: '/campaigns/edit/:id',
          name: 'campaigns-edit',
          component: lazyLoad('views/campaigns/edit/Index'),
          beforeEnter: hasAnyPermission('edit_personal_campaigns', 'edit_all_campaigns')
        },
        {
          path: '/campaigns/duplicate/:duplicate',
          name: 'campaigns-duplicate',
          component: lazyLoad('views/campaigns/create/Index'),
          beforeEnter: hasPermission('create_campaigns')
        },
        {
          path: '/campaigns/:id/tracking',
          name: 'campaigns-tracking',
          component: lazyLoad('views/campaigns/tracking/Tracking'),
          beforeEnter: hasPermission('campaigns_insights')
        },
        {
          path: '/logs',
          name: 'system-logs',
          component: lazyLoad('views/logs/Index'),
          beforeEnter: multiguard([hasPermission('view_system_logs'), hasFeature('system-logs')])
        },
        {
          path: 'settings',
          name: 'settings',
          component: lazyLoad('views/settings/Settings'),
          children: [
            {
              path: 'personal',
              name: 'personal',
              component: lazyLoad('views/settings/personal/Personal'),
              beforeEnter: hasPermission('personal_settings')
            },
            {
              path: 'account',
              name: 'account',
              component: lazyLoad('views/settings/account/Account'),
              beforeEnter: hasPermission('view_personal_account_settings')
            },
            {
              path: 'users',
              name: 'users',
              component: lazyLoad('views/settings/users/Users'),
              beforeEnter: hasPermission('view_sub_users')
            },
            {
              path: 'users/:id',
              name: 'user-details',
              component: lazyLoad('views/settings/users/UserDetails'),
              beforeEnter: hasPermission('view_sub_users', 'edit_sub_users')
            },
            {
              path: 'subaccounts',
              name: 'subaccounts',
              component: lazyLoad('views/settings/subaccounts/Subaccounts'),
              beforeEnter: canAccess('sub-accounts', 'view_sub_accounts')
            },
            {
              path: 'subaccounts/:id',
              name: 'subaccount-details',
              component: lazyLoad('views/settings/subaccounts/SubAccountDetails'),
              beforeEnter: canAccess('view_sub_accounts')
            }
          ]
        },
        {
          path: '/users',
          name: 'users',
          component: lazyLoad('views/users/Users'),
          beforeEnter: hasPermission('view_all_users')
        },
        {
          path: '/users/:id',
          name: 'users-details',
          component: lazyLoad('views/users/UsersDetails'),
          beforeEnter: hasPermission('view_all_users')
        },
        {
          path: '/fund',
          name: 'fund',
          component: lazyLoad('views/fund/Fund'),
          async beforeEnter (from, to, next) {
            return hasPermission('add_personal_fund')(from, to, next)
          }
        },
        {
          path: '/features',
          name: 'features',
          component: lazyLoad('views/features/index'),
          beforeEnter: hasPermission('features')
        },
        {
          path: '/features/:id',
          name: 'features-details',
          component: lazyLoad('views/features/FeatureDetails'),
          beforeEnter: hasPermission('features')
        },
        {
          path: '/accounts',
          name: 'accounts',
          component: lazyLoad('views/accounts/Accounts'),
          beforeEnter: hasPermission('view_all_accounts')
        },
        {
          path: '/accounts/:id',
          name: 'accounts-view',
          component: lazyLoad('views/settings/account/Account'),
          beforeEnter: hasPermission('view_all_accounts')
        },
        {
          path: '/admin/fund',
          name: 'admin-fund',
          component: lazyLoad('views/admin/fund'),
          beforeEnter: hasPermission('control_fund')
        },
        {
          path: '/reports',
          name: 'reports',
          component: lazyLoad('views/reports/index'),
          beforeEnter: hasPermission('personal_reports')
        },
        {
          path: '/admin/min-bid',
          name: 'admin-bid',
          component: lazyLoad('views/settings/BidConfigurations/MinBid'),
          beforeEnter: hasPermission('min_bid')
        },
        {
          path: '/offline',
          name: 'offline',
          component: lazyLoad('views/errors/Offline')
        }
      ],
      beforeEnter: beforeEnterAuthedRoutes
    },
    // Full page layout routes
    {
      path: '',
      component: lazyLoad('layouts/full-page/FullPage'),
      children: [
        {
          path: '/login',
          name: 'login',
          component: lazyLoad('views/auth/Login')
        },
        {
          path: '/register',
          name: 'register',
          component: lazyLoad('views/auth/Register')
        },
        {
          path: '/forget-password',
          name: 'forgetPassword',
          component: lazyLoad('views/auth/password/ForgetPassword')
        },
        {
          path: '/reset-password',
          name: 'resetPassword',
          component: lazyLoad('views/auth/password/ResetPassword')
        }
      ],
      beforeEnter: beforeEnterPublicRoutes
    },
    {
      path: '',
      component: lazyLoad('layouts/full-page/FullPage'),
      children: [
        {
          path: '/404',
          name: 'page-error-404',
          component: lazyLoad('views/errors/Error404')
        },
        {
          path: '/maintenance',
          name: 'maintenance.view',
          component: lazyLoad('views/maintenance/index')
        }
      ]
    },
    // Redirect to 404 page, if no match found
    {
      path: '*',
      redirect: '/404'
    }
  ]
})

// After Milddlewares
router.afterEach(() => {
  // Remove initial loading
  const appLoading = document.getElementById('loading-bg')
  if (appLoading) {
    appLoading.style.display = 'none'
  }
})

// Before Milddlewares
router.beforeEach((to, from, next) => {

  const isMaintenanceModeEnabled = (process.env.VUE_APP_MAINTENANCE_MODE === 'true') || false
  if (to.name !== 'maintenance.view' && isMaintenanceModeEnabled) {
    next('/maintenance')
  }
  if (to.name !== 'offline') {
    if (!navigator.onLine) {
      next('/offline')
      return
    }
  }
  next()
})

export default router
